import { useEffect, useRef, useState } from 'react';
import { setTokens } from '../lib/api';
import { authCall, recoverAfterUsed } from '../lib/authCall';
import { hello, onStepUpCancelled, requestStepUp, stepUpDone } from '../lib/glasses';
import { abortableSleep, pollRequest, pollStepUp, startHandoffStepUp } from '../lib/handoffStepup';
import type { StepUpStart } from '../lib/handoffStepup';
import { ErrorNote } from '../app/ui';

type Phase =
  | { k: 'idle' }
  | { k: 'starting' }
  | { k: 'waiting'; start: StepUpStart; glasses: boolean }
  | { k: 'done' }
  | { k: 'failed'; message: string };

/**
 * O1/O2/O3 (Erwin 2026-10-29): a Helm XR web window cannot use a passkey, so it asks for a step-up code that is
 * approved with a passkey on another device (or "Use this headset" in the glasses), and polls for the result.
 * `onDone` runs after the stepped-up access token is stored (the refresh token is kept).
 */
export function HandoffStepUp({ onDone }: { onDone: () => Promise<void> | void }) {
  const [phase, setPhase] = useState<Phase>({ k: 'idle' });
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => { abort.current?.abort(); }, []);

  async function begin() {
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setPhase({ k: 'starting' });
    const [res, bridge] = await Promise.all([startHandoffStepUp(authCall), hello()]);
    if (ctl.signal.aborted) return;
    if (!res.ok) { setPhase({ k: 'failed', message: res.message }); return; }
    const { start } = res;
    const off = onStepUpCancelled(() => ctl.abort());
    if (bridge) requestStepUp({ userCode: start.userCode, verificationUri: start.verificationUri, expiresIn: start.expiresIn, admin: start.admin, headset: start.headset });
    setPhase({ k: 'waiting', start, glasses: !!bridge });
    const out = await pollStepUp(start, { poll: pollRequest(authCall, start.stepupCode), sleep: abortableSleep, signal: ctl.signal, recover: recoverAfterUsed });
    off();
    if (abort.current !== ctl) return;   // superseded by "Try again" / unmounted
    if (out.kind === 'ok') {
      setTokens(out.accessToken);   // keeps the refresh token
      if (bridge) stepUpDone();
      setPhase({ k: 'done' });
      await onDone();
      return;
    }
    setPhase({ k: 'failed', message: out.message });
  }

  if (phase.k === 'idle' || phase.k === 'starting') {
    return (
      <>
        <p className="muted">This Helm XR window cannot use a passkey itself. Confirm on the glasses, or approve with your passkey on your phone or Mac.</p>
        <button className="btn primary" type="button" onClick={() => void begin()} disabled={phase.k === 'starting'}>{phase.k === 'starting' ? 'Starting…' : 'Confirm it’s you'}</button>
      </>
    );
  }
  if (phase.k === 'waiting') {
    const { start, glasses } = phase;
    return glasses ? (
      <>
        <p>Choose how to confirm in the glasses…</p>
        <p className="muted small">Code: <span className="mono">{start.userCode}</span></p>
      </>
    ) : (
      <>
        <p className="muted">Approve on your phone or Mac with your passkey:</p>
        <p className="mono" style={{ fontSize: '1.6em', letterSpacing: '0.1em' }}>{start.userCode}</p>
        <p><a href={start.verificationUri} target="_blank" rel="noreferrer">{start.verificationUri}</a></p>
        <p className="muted small">Waiting for the approval…</p>
      </>
    );
  }
  if (phase.k === 'done') return <p className="muted">Confirmed.</p>;
  return (
    <>
      <ErrorNote text={phase.message} />
      <button className="btn primary" type="button" onClick={() => void begin()}>Try again</button>
    </>
  );
}
